/**
 * GenericEvidenceVault — /project/:id/evidence-vault
 * Evidence verification workflow with role-based permissions:
 *   - Owner/Analyst: upload + submit for review
 *   - Admin only: verify / reject / needs_clarification
 *   - No self-certification (uploader cannot verify own evidence)
 *
 * Confidence impact: verified items → primary_data score → report watermark
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, CheckCircle2, AlertCircle, Upload,
  FolderOpen, ArrowRight, AlertTriangle, Shield, XCircle,
  ChevronDown, ChevronUp, Send, MessageSquare, CheckCheck, HelpCircle,
  ExternalLink, Copy, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { useProjectWorkspace, invalidateProjectWorkspaceCache } from "@/hooks/useProjectWorkspace";
import { PageHeader, SectionCard } from "@/components/ds";
import client from "@/api/client";

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  verified:           { label: "Verified",               dot: "bg-emerald-500", bg: "bg-emerald-50 border-emerald-200",   text: "text-emerald-700", icon: CheckCircle2 },
  under_review:       { label: "Under Review",           dot: "bg-blue-500",    bg: "bg-blue-50 border-blue-200",         text: "text-blue-700",    icon: AlertCircle },
  uploaded:           { label: "Uploaded",               dot: "bg-amber-400",   bg: "bg-amber-50 border-amber-200",       text: "text-amber-700",   icon: AlertCircle },
  needs_clarification:{ label: "Needs Clarification",    dot: "bg-orange-400",  bg: "bg-orange-50 border-orange-200",     text: "text-orange-700",  icon: HelpCircle },
  rejected:           { label: "Rejected",               dot: "bg-red-500",     bg: "bg-red-50 border-red-200",           text: "text-red-700",     icon: XCircle },
  missing:            { label: "Not Uploaded",           dot: "bg-slate-300",   bg: "bg-slate-50 border-slate-200",       text: "text-slate-400",   icon: AlertCircle },
};

// ── Evidence row with role-aware actions ──────────────────────────────────
function EvidenceRow({ param, req, evidenceItem, assessmentId, onRefresh, fieldValue, isAdmin, currentUserEmail }) {
  const [uploading,    setUploading]    = useState(false);
  const [reviewing,    setReviewing]    = useState(false);
  const [showReview,   setShowReview]   = useState(false);
  const [reviewComment,setReviewComment]= useState("");
  const [rejectReason, setRejectReason] = useState("");

  const status = evidenceItem?.verification_status || "missing";
  const cfg    = STATUS_CFG[status] || STATUS_CFG.missing;
  const Icon   = cfg.icon;

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      let evId = evidenceItem?.id;
      if (!evId) {
        const linkRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${assessmentId}/evidence/link`,
          { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ linked_parameter: param, evidence_type: req.type || "document", label: req.label || param }) }
        );
        if (!linkRes.ok) throw new Error("Link failed");
        evId = (await linkRes.json()).id;
      }
      const fd = new FormData();
      fd.append("file", file);
      const upRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${assessmentId}/evidence/${evId}/upload`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      if (!upRes.ok) throw new Error("Upload failed");
      toast.success(`${req.label} uploaded.`);
      invalidateProjectWorkspaceCache(assessmentId);
      onRefresh();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    setUploading(false);
  };

  const handleReview = async (newStatus) => {
    if (!evidenceItem?.id) return;
    setReviewing(true);
    try {
      const res = await client.patch(
        `/workspace/project/${assessmentId}/evidence/${evidenceItem.id}/review`,
        { verification_status: newStatus, review_comment: reviewComment || null,
          rejection_reason: rejectReason || null }
      );
      toast.success(`Evidence ${newStatus === "verified" ? "verified ✓" : newStatus === "rejected" ? "rejected" : "status updated"}.`);
      setShowReview(false); setReviewComment(""); setRejectReason("");
      invalidateProjectWorkspaceCache(assessmentId);
      onRefresh();
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.message || e.message || "Review action failed.");
    }
    setReviewing(false);
  };

  const handleSubmitForReview = async () => {
    if (!evidenceItem?.id) return;
    setReviewing(true);
    try {
      await client.patch(`/workspace/project/${assessmentId}/evidence/${evidenceItem.id}/review`,
        { verification_status: "under_review" });
      toast.success("Submitted for review.");
      invalidateProjectWorkspaceCache(assessmentId);
      onRefresh();
    } catch (e) { toast.error("Failed to submit for review."); }
    setReviewing(false);
  };

  const canSelfCertify = isAdmin && evidenceItem?.uploaded_by !== currentUserEmail;

  return (
    <div className={`border rounded-xl overflow-hidden ${cfg.bg}`} data-testid={`evidence-row-${param}`}>
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${cfg.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-xs font-bold text-slate-900">{req.label || param}</p>
              {req.critical && <span className="text-xs font-bold px-1.5 py-0 rounded bg-red-100 text-red-600 border border-red-200">CRITICAL</span>}
              {req.audit_ready_required && <span className="text-xs px-1.5 py-0 rounded bg-slate-100 text-slate-500 border border-slate-200">Audit Required</span>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{req.hint || req.type?.replace(/_/g," ")}</p>
            {fieldValue && fieldValue !== "undefined" && fieldValue !== "null" && (
              <p className="text-xs text-teal-600 mt-0.5">
                Input value: <strong>{fieldValue}</strong> — evidence links this to verifiable documentation
              </p>
            )}
            {evidenceItem?.file_name && (
              <p className="text-xs text-slate-600 mt-0.5 font-mono">{evidenceItem.file_name}</p>
            )}
            {/* Reviewer info */}
            {evidenceItem?.reviewed_by && (
              <p className="text-xs text-slate-400 mt-0.5">
                {status === "verified" ? "Verified" : status === "rejected" ? "Rejected" : "Reviewed"} by {evidenceItem.reviewed_by}
                {evidenceItem.reviewed_at && ` · ${evidenceItem.reviewed_at.slice(0,10)}`}
              </p>
            )}
            {evidenceItem?.review_comment && (
              <p className="text-xs text-slate-500 mt-0.5 italic">"{evidenceItem.review_comment}"</p>
            )}
            {evidenceItem?.rejection_reason && (
              <p className="text-xs text-red-600 mt-0.5 font-semibold">Reason: {evidenceItem.rejection_reason}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
          </span>

          {/* Upload button */}
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} />
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
              uploading ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}>
              {uploading ? <RefreshCw size={10} className="animate-spin" /> : <Upload size={10} />}
              {status === "missing" ? "Upload" : "Replace"}
            </div>
          </label>

          {/* Submit for Review — non-admin, item uploaded */}
          {!isAdmin && status === "uploaded" && evidenceItem?.id && (
            <button onClick={handleSubmitForReview} disabled={reviewing}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              data-testid={`submit-review-${param}`}>
              {reviewing ? <RefreshCw size={10} className="animate-spin" /> : <Send size={10} />}
              Submit for Review
            </button>
          )}

          {/* Admin review toggle — on under_review items */}
          {isAdmin && status === "under_review" && (
            <button onClick={() => setShowReview(v => !v)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-white border-slate-200 text-slate-600 hover:bg-slate-50">
              <MessageSquare size={10} /> Review
              {showReview ? <ChevronUp size={9}/> : <ChevronDown size={9}/>}
            </button>
          )}

          {/* Re-review rejected/clarification items */}
          {isAdmin && status in {rejected:1, needs_clarification:1} && (
            <button onClick={() => setShowReview(v => !v)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-white border-slate-200 text-slate-600 hover:bg-slate-50">
              <MessageSquare size={10} /> Re-review
            </button>
          )}
        </div>
      </div>

      {/* Admin review panel */}
      {showReview && isAdmin && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 space-y-3">
          <p className="text-xs font-bold text-slate-700">Review Decision</p>

          {/* Self-certification warning */}
          {evidenceItem?.uploaded_by === currentUserEmail && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">You uploaded this item. Assign another admin to verify (independence principle).</p>
            </div>
          )}

          <textarea
            value={reviewComment}
            onChange={e => setReviewComment(e.target.value)}
            placeholder="Review comment (optional but recommended)"
            rows={2}
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400 resize-none"
          />

          {/* Rejection reason */}
          <input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Rejection reason (required for reject / needs clarification)"
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-red-400"
          />

          <div className="flex items-center gap-2">
            <button onClick={() => handleReview("verified")} disabled={reviewing || evidenceItem?.uploaded_by === currentUserEmail}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid={`verify-btn-${param}`}>
              {reviewing ? <RefreshCw size={10} className="animate-spin" /> : <CheckCheck size={10} />}
              Verify ✓
            </button>
            <button onClick={() => handleReview("needs_clarification")} disabled={reviewing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600 disabled:opacity-50"
              data-testid={`clarify-btn-${param}`}>
              <HelpCircle size={10} /> Needs Clarification
            </button>
            <button onClick={() => handleReview("rejected")} disabled={reviewing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 disabled:opacity-50"
              data-testid={`reject-btn-${param}`}>
              <XCircle size={10} /> Reject ✗
            </button>
            <button onClick={() => setShowReview(false)} className="text-xs text-slate-400 hover:text-slate-600 ml-auto">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section card with evidence items ──────────────────────────────────────
function SectionEvidenceCard({ sectionName, items, itemsByParam, evReqs, assessmentId, onRefresh, inputSnapshot, isAdmin, currentUserEmail }) {
  const [open, setOpen] = useState(true);
  const total    = items.length;
  const verified = items.filter(([p]) => itemsByParam[p]?.verification_status === "verified").length;
  const covered  = items.filter(([p]) => itemsByParam[p]?.verification_status && itemsByParam[p].verification_status !== "missing").length;
  const status   = verified === total ? "complete" : verified > 0 ? "partial" : covered > 0 ? "partial" : "missing";

  const BADGE = {
    complete: "bg-emerald-100 text-emerald-700 border-emerald-200",
    partial:  "bg-amber-100 text-amber-700 border-amber-200",
    missing:  "bg-red-50 text-red-600 border-red-200",
  };
  const BADGE_LABEL = { complete: "Complete", partial: "Partial", missing: "Missing" };

  const criticalMissing = items.filter(([p, r]) => r.critical && (!itemsByParam[p] || itemsByParam[p].verification_status === "missing")).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
      <button className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-50 hover:bg-slate-100 text-left" onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-3">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">{sectionName}</p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${BADGE[status]}`}>{BADGE_LABEL[status]}</span>
          {criticalMissing > 0 && (
            <span className="text-xs text-red-600 font-semibold flex items-center gap-1"><AlertTriangle size={10} /> {criticalMissing} critical missing</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{verified}/{total} verified</span>
          {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-5 py-4 space-y-3">
          {items.map(([param, req]) => (
            <EvidenceRow key={param} param={param} req={req} evidenceItem={itemsByParam[param]}
              assessmentId={assessmentId} onRefresh={onRefresh}
              fieldValue={inputSnapshot?.[param] != null ? String(inputSnapshot[param]) : null}
              isAdmin={isAdmin} currentUserEmail={currentUserEmail} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Confidence impact panel ────────────────────────────────────────────────
function ConfidenceImpactPanel({ evidence, evReqs, dataConfidence }) {
  const verifiedCount   = Object.values(evidence?.items || []).filter(e => e.verification_status === "verified").length;
  const totalRequired   = Object.keys(evReqs).length;
  const currentScore    = dataConfidence?.data_confidence_score || 0;
  const primaryData     = dataConfidence?.confidence_breakdown?.primary_data || 0;

  if (totalRequired === 0) return null;

  const maxPrimaryData      = 60;
  const perItemPoints       = totalRequired > 0 ? maxPrimaryData / totalRequired : 0;
  const verifiedPointsGain  = Math.round(perItemPoints * (totalRequired - verifiedCount));
  const projectedScore      = Math.min(100, Math.round(currentScore + verifiedPointsGain));
  const toUnlock60          = Math.max(0, 60 - currentScore);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="confidence-impact">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Confidence Score Impact</p>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
          currentScore >= 60 ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
          currentScore >= 40 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-50 text-red-600 border-red-200"
        }`}>{currentScore}/100 ({dataConfidence?.label || "Low"})</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          ["Primary Data", `${primaryData}/60`, "Increases with verified evidence"],
          ["Verified Items", `${verifiedCount}/${totalRequired}`, `Each verified = ~${perItemPoints.toFixed(1)} pts`],
          ["Target Score", "60/100", "Required for watermark removal"],
        ].map(([l, v, sub]) => (
          <div key={l} className="bg-slate-50 rounded-lg px-3 py-2.5 text-center">
            <p className="text-xs text-slate-400">{l}</p>
            <p className="text-sm font-bold text-slate-900">{v}</p>
            <p className="text-xs text-slate-400 italic">{sub}</p>
          </div>
        ))}
      </div>
      {currentScore < 60 ? (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            Verify more evidence to unlock investment grade. Need +{toUnlock60} pts →
            verify ~{Math.ceil(toUnlock60 / perItemPoints)} more items to reach 60/100.
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
          <p className="text-xs text-emerald-700 font-semibold">Confidence threshold met. INVESTMENT_READY + baseline confirmed removes watermark.</p>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function GenericEvidenceVault() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    loading, sectorCfg, sector, sectorLabel, org,
    evidence, evidenceCoverage, refresh, inputSnapshot, journey, projectState,
  } = useProjectWorkspace(id);

  const [verRequest,    setVerRequest]    = useState(null);
  const [requestLoading,setRequestLoading]= useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [inviteEmail,   setInviteEmail]   = useState("");

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem("decarboniq_user") || "{}"); }
    catch { return {}; }
  })();
  const isAdmin          = currentUser.role === "admin";
  const currentUserEmail = currentUser.email || "";

  // Load current verification request status
  React.useEffect(() => {
    if (!id) return;
    client.get(`/workspace/project/${id}/verification-request`)
      .then(r => setVerRequest(r.data))
      .catch(() => {});
  }, [id]);

  const handleRequestVerification = async () => {
    setRequestLoading(true);
    try {
      const res = await client.post(`/workspace/project/${id}/request-verification`, {
        verifier_email_invited: inviteEmail || null,
      });
      setVerRequest({ has_request: true, status: "requested", link: res.data.link, ...res.data });
      setShowRequestModal(false);
      toast.success("Verification request created. Share the link with your independent verifier.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create request.");
    }
    setRequestLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>
  );

  const evReqs       = sectorCfg?.evidence_requirements || {};
  const evItems      = evidence?.items || [];
  const itemsByParam = {};
  evItems.forEach(e => { itemsByParam[e.linked_parameter] = e; });
  const dataConfidence = journey?.data_confidence || {};

  // Verification tier from CDE
  const verReport = projectState?.report || {};
  const verLevel  = verReport.verification_level || "preliminary";
  const verLabel  = verReport.verification_label || "Preliminary";
  const VER_TIER_COLORS = {
    preliminary:          "border-amber-200 bg-amber-50 text-amber-700",
    uploaded:             "border-blue-200 bg-blue-50 text-blue-700",
    reviewed_internal:    "border-teal-200 bg-teal-50 text-teal-800",
    verified_independent: "border-emerald-300 bg-emerald-50 text-emerald-800",
    certified:            "border-emerald-400 bg-emerald-100 text-emerald-900",
  };

  const coverPct        = evidenceCoverage.coverage_pct || 0;
  const criticalMissing = evidenceCoverage.critical_missing || [];
  const totalRequired   = Object.keys(evReqs).length;
  const totalCovered    = evItems.filter(e => evReqs[e.linked_parameter] && e.verification_status !== "missing").length;
  const totalVerified   = evItems.filter(e => evReqs[e.linked_parameter] && e.verification_status === "verified").length;
  const isAuditReady    = coverPct >= 70 && criticalMissing.length === 0;

  const sections = {};
  Object.entries(evReqs).forEach(([param, req]) => {
    const section = req.section || req.category || "General";
    if (!sections[section]) sections[section] = [];
    sections[section].push([param, req]);
  });
  const SECTION_ORDER = ["Feedstock", "Baseline", "Biogas System", "Financial", "Offtake & Commercial", "Feedstock Supply Security"];
  const sortedSections = [
    ...SECTION_ORDER.filter(s => sections[s]),
    ...Object.keys(sections).filter(s => !SECTION_ORDER.includes(s)),
  ];

  return (
    <>
      <div className="p-6 lg:p-8 min-h-screen pb-24">
        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> {sectorLabel}
        </button>

        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
          <PageHeader title="Evidence Vault" />
          <p className="text-xs text-slate-400 mt-0.5">Field-linked evidence for MRV verification · Protocol: {sectorCfg?.protocol || sector}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
              isAdmin ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-600 border-blue-200"
            }`}>
              {isAdmin ? "Admin — can verify/reject" : "Owner — can upload and submit for review"}
            </span>
            {/* Current verification tier */}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${VER_TIER_COLORS[verLevel] || VER_TIER_COLORS.preliminary}`}
              data-testid="vault-verification-tier" title="Current verification tier">
              {verLabel}
            </span>
          </div>
        </div>

        {/* Verification tier explanation panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="verification-tier-panel">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">Verification Tier System</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { level: "uploaded",             label: "Submitted",             desc: "Evidence uploaded, awaiting admin review", tier: 1 },
              { level: "reviewed_internal",    label: "Internally Verified",   desc: "Admin-reviewed. NOT independent verification.", tier: 2 },
              { level: "verified_independent", label: "Independent Verification Not Initiated", desc: "Verification by accredited external party (future workflow)", tier: 3 },
              { level: "certified",            label: "Certified",             desc: "Full certification by accredited body (future)", tier: 4 },
            ].map(t => {
              const isActive = verLevel === t.level;
              const isPast   = ["uploaded","reviewed_internal","verified_independent","certified"].indexOf(verLevel)
                             >= ["uploaded","reviewed_internal","verified_independent","certified"].indexOf(t.level);
              return (
                <div key={t.level} className={`rounded-lg px-3 py-2.5 border ${
                  isActive ? `${VER_TIER_COLORS[t.level]} border-2` :
                  isPast   ? "bg-slate-50 border-slate-200 opacity-80" :
                             "bg-slate-50 border-slate-200 opacity-40"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs text-slate-400">T{t.tier}</span>
                    <p className="text-xs font-bold text-slate-700">{t.label}</p>
                    {isActive && <span className="text-xs text-teal-600 ml-auto">← current</span>}
                  </div>
                  <p className="text-xs text-slate-400 italic">{t.desc}</p>
                </div>
              );
            })}
          </div>
          {verLevel === "reviewed_internal" && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2">
              <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Admin verification = <strong>internal review only</strong>. This does NOT constitute independent third-party validation.
                For carbon market use, engage an accredited VVB (Validation/Verification Body).
              </p>
            </div>
          )}
        </div>

        {/* T2 → T3 CTA: Request Independent Verification */}
        {verLevel === "reviewed_internal" && (
          <div className="bg-white border-2 border-teal-200 rounded-xl p-5 mb-5" data-testid="request-verification-cta">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-teal-800 mb-1">Ready for Independent Verification</p>
                <p className="text-xs text-slate-600">
                  Internal review complete. Generate a secure link to share with an accredited external verifier.
                  Verifier approval records independent verification as initiated.
                </p>
              </div>
              <button onClick={() => setShowRequestModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 flex-shrink-0"
                data-testid="request-verification-btn">
                <Link2 size={13} /> Request Independent Verification
              </button>
            </div>

            {/* Show active request status */}
            {verRequest?.has_request && (
              <div className={`mt-4 pt-4 border-t border-teal-100 flex items-center justify-between gap-3 ${
                verRequest.status === "approved" ? "text-emerald-700" :
                verRequest.status === "rejected" ? "text-red-700" : "text-teal-700"
              }`}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide">
                    {verRequest.status === "requested" ? "Verification Requested" :
                     verRequest.status === "reviewing" ? "Under Independent Review" :
                     verRequest.status === "approved"  ? "Verification Approved ✓" :
                     verRequest.status === "rejected"  ? "Verification Rejected" : ""}
                  </p>
                  {verRequest.link && verRequest.status !== "approved" && verRequest.status !== "rejected" && (
                    <p className="text-xs text-slate-500 mt-0.5 break-all">{verRequest.link}</p>
                  )}
                </div>
                {verRequest.link && verRequest.status !== "approved" && (
                  <button onClick={() => { navigator.clipboard.writeText(verRequest.link); toast.success("Link copied!"); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-teal-300 text-teal-700 text-xs font-semibold rounded-lg hover:bg-teal-50 flex-shrink-0">
                    <Copy size={11} /> Copy Link
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* T3 status: Already independently verified */}
        {verLevel === "verified_independent" && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 mb-5" data-testid="t3-verified-status">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Verification Approved</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  An accredited external verifier has reviewed and approved this project.
                  Report watermark removed. Prepared for formal DFI submission and carbon market engagement.
                  Further validation steps may still be required for credit issuance.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Request Independent Verification Modal */}
        {showRequestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" data-testid="request-verification-modal">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">Request Independent Verification</p>
                <button onClick={() => setShowRequestModal(false)} className="text-xs text-slate-400 hover:text-slate-600">✕</button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-xs text-amber-800">
                  This generates a secure read-only link. Your independent verifier views the project summary, evidence, and outputs — and approves or rejects.
                  <strong> The verifier MUST NOT be a project owner or internal admin.</strong>
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Verifier email (optional — for records only)</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="dr.verifier@auditing-firm.com"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleRequestVerification} disabled={requestLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50">
                  {requestLoading ? <RefreshCw size={14} className="animate-spin" /> : <Link2 size={14} />}
                  Generate Verification Link
                </button>
                <button onClick={() => setShowRequestModal(false)} className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-50">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Audit-Ready gate — renamed to Validation Readiness */}
        <div className={`border-2 rounded-xl p-5 mb-5 ${isAuditReady ? "border-teal-300 bg-teal-50" : "border-amber-300 bg-amber-50"}`} data-testid="audit-ready-gate">
          <div className="flex items-start gap-3">
            <Shield size={20} className={isAuditReady ? "text-teal-600" : "text-amber-600"} />
            <div className="flex-1">
              <p className={`text-sm font-bold ${isAuditReady ? "text-teal-800" : "text-amber-800"}`}>
                {isAuditReady
                  ? "Validation Readiness Threshold Met — Independent Verification Required to proceed"
                  : `Evidence Incomplete — 70% coverage required (current: ${coverPct}%)`}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {totalVerified}/{totalRequired} verified · {totalCovered}/{totalRequired} uploaded · {criticalMissing.length} critical missing
              </p>
            </div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full flex-shrink-0 ${
              isAuditReady ? "bg-teal-200 text-teal-800" : "bg-amber-200 text-amber-800"
            }`}>{coverPct}%</span>
          </div>

          {/* Clarity block — data completeness ≠ verification */}
          <div className="mt-4 pt-4 border-t border-current/10 bg-white/40 rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Important Distinctions</p>
            <div className="space-y-1.5">
              {[
                ["Data Completeness", "≠ Verification", "Percentage of required fields filled and evidence uploaded"],
                ["Model Confidence",  "≠ Certification", "Statistical confidence in model outputs — not equivalent to third-party certification"],
                ["Admin Review",      "≠ Independent Validation", "Internal review confirms data quality — does NOT substitute for accredited VVB validation"],
              ].map(([a, b, note]) => (
                <div key={a} className="flex items-start gap-2 text-xs">
                  <span className="font-bold text-slate-700 flex-shrink-0">{a}</span>
                  <span className="text-red-600 font-bold flex-shrink-0">{b}</span>
                  <span className="text-slate-500 italic">— {note}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700 font-semibold mt-2">
              Independent verification by an accredited VVB is required for carbon credit issuance or formal DFI submission.
            </p>
          </div>

          {/* Confidence formula */}
          <div className="mt-3 bg-white/40 rounded-xl px-4 py-2.5">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Confidence Score Formula</p>
            <p className="text-xs font-mono text-slate-700">
              Confidence Score = f(data_coverage × tier_quality × evidence_verification × assumption_dependency)
            </p>
            <p className="text-xs text-slate-400 italic mt-1">
              Current: {totalVerified}/{totalRequired} verified × tier multiplier × baseline confirmation × assumption flags
            </p>
          </div>

          {/* Coverage bars */}
          <div className="mt-4 space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-slate-700">Evidence Uploaded</span>
                <span className="font-bold">{totalCovered}/{totalRequired} ({coverPct}%)</span>
              </div>
              <div className="h-2.5 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${isAuditReady ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${coverPct}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-500">Evidence Verified (for confidence)</span>
                <span className={`font-semibold ${totalVerified === totalRequired ? "text-emerald-600" : "text-slate-600"}`}>
                  {totalVerified}/{totalRequired} · +{Math.round((totalVerified/Math.max(totalRequired,1)) * 60)}pts primary_data
                </span>
              </div>
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${(totalVerified/Math.max(totalRequired,1))*100}%` }} />
              </div>
            </div>
          </div>
          {criticalMissing.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200">
              <p className="text-xs font-bold text-red-600 mb-1.5 flex items-center gap-1"><AlertTriangle size={10} /> Critical evidence missing:</p>
              <div className="space-y-0.5">
                {criticalMissing.map(p => (
                  <div key={p} className="flex items-center gap-1.5 text-xs">
                    <XCircle size={9} className="text-red-400 flex-shrink-0" />
                    <span className="text-red-700">{evReqs[p]?.label || p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Confidence impact panel */}
        <ConfidenceImpactPanel evidence={evidence} evReqs={evReqs} dataConfidence={dataConfidence} />

        {/* Section-by-section evidence */}
        {sortedSections.length > 0 ? (
          <>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
              Evidence by Section — {totalRequired} fields across {sortedSections.length} sections
            </p>
            {sortedSections.map(sectionName => (
              <SectionEvidenceCard key={sectionName} sectionName={sectionName}
                items={sections[sectionName]} itemsByParam={itemsByParam} evReqs={evReqs}
                assessmentId={id} onRefresh={refresh} inputSnapshot={inputSnapshot}
                isAdmin={isAdmin} currentUserEmail={currentUserEmail} />
            ))}
          </>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center mb-6">
            <FolderOpen size={24} className="text-amber-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-amber-800">No evidence requirements defined</p>
          </div>
        )}

        {/* Reference standards */}
        {sectorCfg?.reference_standards?.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mt-4">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Reference Standards</p>
            {sectorCfg.reference_standards.map((r, i) => <p key={i} className="text-xs text-slate-500">· {r}</p>)}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${isAuditReady ? "bg-emerald-500" : "bg-amber-500"}`} />
          <p className="text-xs text-slate-500">
            {isAuditReady ? "Validation readiness threshold met — independent verification required" : `${totalRequired - totalCovered} item(s) remaining`}
            {totalVerified > 0 && ` · ${totalVerified} verified`}
          </p>
        </div>
        <button onClick={() => navigate(`/project/${id}/carbon-strategy`)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800"
          data-testid="proceed-to-carbon-strategy">
          Carbon Strategy →
        </button>
      </div>
    </>
  );
}
